import { Box, Flex, Heading, Spinner, TabNav } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { PitchDetailsTab } from 'components/common/dialogs/pitch-data/details.tab';
import { InspectorTab } from 'components/common/dialogs/pitch-data/inspector.tab';
import { TrainingDataTab } from 'components/common/dialogs/pitch-data/training-data.tab';
import { ErrorBoundary } from 'components/common/error-boundary';
import { PitchDesign } from 'components/sections/pitch-design';
import { MachineContext } from 'contexts/machine.context';
import { PitchDataMode } from 'enums/pitches';
import { t } from 'i18next';
import { TrainPitchesFn } from 'interfaces/function-types';
import { IBaseDialog } from 'interfaces/i-dialogs';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import { DEFAULT_PLATE, IPitch } from 'lib_ts/interfaces/pitches';
import { DEFAULT_SEAMS } from 'lib_ts/interfaces/pitches/i-base';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import { usePitchDesignStore } from 'stores/use-pitch-design-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

enum TabKey {
  Details = 'common.pitch-details',
  TrainingData = 'common.training-data',
  PitchDesign = 'common.pitch-design',
  MsInspector = 'common.inspector',
}

interface IProps extends IBaseDialog {
  // e.g. disable training data when in Game Data
  mode: PitchDataMode;
  pitch: IPitch;

  onTrainFn?: TrainPitchesFn;
  onPitchUpdated?: (pitch: IPitch) => void;
}

export const PitchDataDialog = (props: IProps) => {
  const { machine } = useContext(MachineContext);

  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const [activeTab, setActiveTab] = useState(TabKey.Details);
  const [ms, setMs] = useState<IMachineState>();

  const trainingShots = useTrainedMsStore(
    (s) => s.trainingShotsDict[props.pitch._mx_tms?._id ?? '']
  );

  useEffect(() => {
    useTrainedMsStore
      .getState()
      .lazyLoadMs({
        machine: machine,
        bs_hash: props.pitch.bs_hash,
        chars: {
          build_id: props.pitch._id,
          priority: props.pitch.priority ?? BuildPriority.Default,
          bs: props.pitch.bs,
          bs_hash: props.pitch.bs_hash,
          traj: props.pitch.traj,
          plate: props.pitch.plate_loc_backup ?? DEFAULT_PLATE,
          seams: props.pitch.seams ?? DEFAULT_SEAMS,
          breaks: props.pitch.breaks,
        },
      })
      .then((result) => setMs(result));
  }, [machine, props.pitch]);

  // load training shots
  useEffect(() => {
    useTrainedMsStore.getState().lazyLoadShots(props.pitch.bs_hash, false);
  }, [props.pitch.bs_hash]);

  // for PitchDesign to render correctly later (if opened)
  useEffect(() => {
    usePitchDesignStore.getState().setRefPitch(props.pitch);
    usePitchDesignStore.setState({
      isNew: false,
      showCurrent: true,
    });
  }, [props.pitch]);

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case TabKey.Details: {
        return (
          <PitchDetailsTab
            pitch={props.pitch}
            shots={trainingShots ?? []}
            readOnly={props.mode === PitchDataMode.GameData}
            onPitchUpdated={props.onPitchUpdated}
          />
        );
      }

      case TabKey.TrainingData: {
        return (
          <TrainingDataTab
            pitch={props.pitch}
            shots={trainingShots ?? []}
            ms={ms}
            onTrainFn={props.onTrainFn}
          />
        );
      }

      case TabKey.PitchDesign: {
        return (
          <PitchDesign
            identifier="PitchDataPitchDesigner"
            onPitchUpdated={props.onPitchUpdated}
            asDialog
          />
        );
      }

      case TabKey.MsInspector: {
        return <InspectorTab pitch={props.pitch} />;
      }

      default: {
        return <Spinner />;
      }
    }
  }, [activeTab, props.pitch, props.mode, trainingShots, ms, props.onTrainFn]);

  const tabBar = useMemo(() => {
    const isDebug = !!props.pitch.debugMs;

    const tabs: {
      label: string;
      value: TabKey;
      invisible?: boolean;
      icon?: ReactNode;
    }[] = [
      {
        label: 'common.pitch-details',
        value: TabKey.Details,
      },
      {
        label: 'common.training-data',
        value: TabKey.TrainingData,
        invisible: isDebug || props.mode === PitchDataMode.GameData,
      },
      {
        label: 'main.pitch-design',
        value: TabKey.PitchDesign,
        invisible: isDebug,
      },
      {
        label: 'common.inspector',
        value: TabKey.MsInspector,
        invisible: !userRoleFeatureFlags.pitchMsDetails,
        icon: <SuperAdminIcon />,
      },
    ];

    return (
      <TabNav.Root>
        {tabs
          .filter((m) => !m.invisible)
          .map((m, i) => (
            <TabNav.Link
              key={i}
              active={activeTab === m.value}
              onClick={() => setActiveTab(m.value)}
            >
              <Flex gap={RADIX.FLEX.GAP.SM} align="center">
                <Box>{t(m.label)}</Box>
                {m.icon ? <Box mt="1">{m.icon}</Box> : null}
              </Flex>
            </TabNav.Link>
          ))}
      </TabNav.Root>
    );
  }, [activeTab, props.pitch, props.mode]);

  return (
    <ErrorBoundary identifier="PitchDataDialog">
      <CommonDialog
        identifier={props.identifier}
        width={
          activeTab === TabKey.PitchDesign
            ? RADIX.DIALOG.WIDTH.XL
            : RADIX.DIALOG.WIDTH.LG
        }
        title={
          <Heading
            onClick={() => {
              if (current.role !== UserRole.admin) {
                return;
              }

              navigator.clipboard.writeText(props.pitch._id);

              NotifyHelper.info({
                message_md: 'Pitch ID copied to clipboard!',
              });
            }}
            size={RADIX.HEADING.SIZE.MODAL_TITLE}
            mb="0"
            truncate
          >
            {t('pl.pitch-data-for-x', {
              x: props.pitch.name ?? t('pl.unnamed-pitch'),
            })}
          </Heading>
        }
        content={
          <Flex direction="column" gap={RADIX.FLEX.GAP.MD} height="100%">
            {tabBar}
            {tabContent}
          </Flex>
        }
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
